import { DataQualityService, SchemaService } from '@amzn/awscat-portfolio-assessment-service-client';
import { DataQualityCreateRule, DataQualityGetRule, 
    Impact, ImpactArea, ImpactSeverity, SchemaClause, SchemaProperty
} from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Alert, Box, Button, Container, Form, FormField, Grid, Header, Icon, Input, Multiselect,
    MultiselectProps, Select, SelectProps, SpaceBetween, Spinner, Table, Textarea
} from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { NonCancelableCustomEvent } from '@amzn/awsui-components-react/polaris/internal/events';
import { useContext, useEffect, useState } from 'react';
import { match, useHistory, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../common/Constants';
import { constants } from '../../common/Localization';
import { ErrorWithResponse, MatchParam } from '../../common/types';
import { deepCopy } from '../../common/util';
import { HelpPanelContext } from '../../contexts/HelpPanelContext';
import ConfirmationModal from '../common/ConfirmationModal';
import { defaultSeverityOption, emptySchemaClause, impactOptions, severityOptions } from './data-quality-constants';
import { getClauseBuilderColumns } from './data-quality-helper';
import { AttributeOptions } from './interfaces';

type Props = FlashContextInterface; 

const dataQualityService = new DataQualityService();
const schemaService = new SchemaService();

function DataQualityCreateOrEditRule({ flash }: Props): JSX.Element {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const editMatch: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/management/assets/rules/edit/:ruleId',
    });
    const portfolioId = match?.params?.portfolioId || '';
    const ruleId = editMatch?.params?.ruleId || '';
    
    const { panelHandler } = useContext(HelpPanelContext);
    const isMounted = useIsMounted();
    const [ originalRuleValues, setOriginalRuleValues ] = useState<DataQualityGetRule>();
    const [ ruleToEdit, setRuleToEdit ] = useState<DataQualityCreateRule>();
    const [ isDefaultRule, setIsDefaultRule ] = useState(false);
    const [ loadingRuleDetails, setLoadingRuleDetails ] = useState(false);
    const [ loadingAttributes, setLoadingAttributes ] = useState(false);
    const [ validateOrSaveLoading, setValidateOrSaveLoading ] = useState(false);
    const [ attributes, setAttributes ] = useState<AttributeOptions[]>([]);
    const [ selectedImpacts, setSelectedImpacts] = useState<OptionDefinition[]>([]);
    const [ isValidated, setIsValidated ] = useState(false);
    const [ validationErrorMsg, setValidationErrorMsg ] = useState<string | null>(null);
    const [ showValidationSuccessAlert, setShowValidationSuccessAlert ] = useState(false);
    const [ configErrorText, setConfigErrorText ] = useState<string | null>(null);
    const [ isClauseBuilderShown, setIsClauseBuilderShown ] = useState(true);
    const [ cancelFrictionVisible, setCancelFrictionVisible ] = useState(false);
    
    const history = useHistory();
    const redirectToAssetsPage = () => {
        history.push(`/portfolio/${portfolioId}/management/assets/servers`);
    };

    const loadRuleDetails = async () => {
        if (!isMounted()) {
            return;
        }
        setLoadingRuleDetails(true);
        try {
            const rule = await dataQualityService.getRule(portfolioId, ruleId);
            if (isMounted()) {
                setRuleToEdit(rule);
                setOriginalRuleValues(rule);
                setIsClauseBuilderShown(!rule.isDefault);
                setIsDefaultRule(rule.isDefault ?? false);
            }
            const selectedImpacts = impactOptions.filter(impact => rule?.impacts?.find(ruleImpact => ruleImpact.area === impact.id));
            if (isMounted()) {
                setSelectedImpacts(selectedImpacts);
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, '');
            }
        } finally {
            if (isMounted()) {
                setLoadingRuleDetails(false);
            }
        }
    };

    useEffect(() => {
        if (ruleId) {
            panelHandler(HelpPanelTopicOptions.DATA_QUALITY_EDIT_RULE);
            loadRuleDetails();
        } else {
            panelHandler(HelpPanelTopicOptions.DATA_QUALITY_CREATE_RULE);
            setRuleToEdit({
                schemaClause: [emptySchemaClause],
                impacts: [],
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ ruleId ]);

    const loadAttributes = async () => {
        if (!isMounted()) {
            return;
        }
        setLoadingAttributes(true);
        try {
            const serverSchema = await schemaService.listAttributes('servers', portfolioId);
            const applicationSchema = await schemaService.listAttributes('applications', portfolioId);
            const databaseSchema = await schemaService.listAttributes('databases', portfolioId);
            let headers = mapSchema(serverSchema, 'Server.', 'server');
            headers = headers.concat(mapSchema(applicationSchema, 'Application.', 'application'));
            headers = headers.concat(mapSchema(databaseSchema, 'Database.', 'database'));
            if (isMounted()) {
                setAttributes(headers);
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, '');
            }
        } finally {
            if (isMounted()) {
                setLoadingAttributes(false);
            }
        }
    };

    const mapSchema = (schemas: SchemaProperty[], headerLabel: string, headerType: string) => {
        return schemas.filter(schema => !schema.disabledForDataQualityRules).map(schema => {
            const option = {
                label: headerLabel + schema.text,
                value: `${headerType}:${schema.value}`,
                type: headerType,
                dataType: schema.type || schema.dataType || 'string'
            };
            return option;
        });
    };

    useEffect(() => {
        if (portfolioId) {
            loadAttributes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ portfolioId ]);

    const toggleShowRawSchema = () => {
        setIsClauseBuilderShown(!isClauseBuilderShown);
    };

    const validateRule = async () => {
        if (!ruleToEdit?.schemaClause) {
            return;
        }
        setValidateOrSaveLoading(true);
        try {
            await dataQualityService.validateClause(portfolioId, ruleToEdit.schemaClause);
            if (isMounted()) {
                setIsValidated(true);
                setShowValidationSuccessAlert(true);
            }
        } catch (err) {
            if (isMounted()) {
                setIsValidated(false);
                setShowValidationSuccessAlert(false);
                setValidationErrorMsg((err as ErrorWithResponse).response?.data?.message ?? (err as Error).message);
            }
        } finally {
            if (isMounted()) {
                setValidateOrSaveLoading(false);
            }
        }
    };

    const editRule = async () => {
        if (!ruleToEdit || !originalRuleValues || !isMounted()) {
            return;
        }
        setValidateOrSaveLoading(true);
        try {
            const updateFields: DataQualityCreateRule = {
                name: ruleToEdit.name,
                description: ruleToEdit.description,
                impacts: ruleToEdit.impacts,
                schemaClause: ruleToEdit.schemaClause,
            };
            await dataQualityService.batchUpdateRules(portfolioId, [originalRuleValues.ruleId], updateFields);
            flash.alert(FlashType.success, constants.UPDATES_SUCCESSFULLY_SAVED, '');
            redirectToAssetsPage();
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, '');
            }
        } finally {
            if (isMounted()) {
                setValidateOrSaveLoading(false);
            }
        }
    };

    const createRule = async () => {
        if (!ruleToEdit || !isMounted()) {
            return;
        }
        setValidateOrSaveLoading(true);
        try {
            await dataQualityService.createRule(portfolioId, ruleToEdit);
            flash.alert(FlashType.success, constants.CREATE_RULE_SUCCESS, '');
            redirectToAssetsPage();
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, '');
            }
        } finally {
            if (isMounted()) {
                setValidateOrSaveLoading(false);
            }
        }
    };

    const saveRule = async () => {
        if (!ruleToEdit) {
            return;
        }
        if (!ruleToEdit.name) {
            setConfigErrorText(constants.MISSING_RULE_NAME);
            return;
        } 
        if (!ruleToEdit.description) {
            setConfigErrorText(constants.MISSING_RULE_DESCRIPTION);
            return;
        }
        if (ruleToEdit.impacts?.length === 0) {
            setConfigErrorText(constants.MISSING_RULE_IMPACT);
            return;
        }

        if (originalRuleValues) {
            editRule();
        } else {
            createRule();
        }  
    };

    const hasRuleChanged = (): boolean => {
        const original = JSON.stringify(originalRuleValues);
        const updated = JSON.stringify(ruleToEdit);
        return original !== updated;
    };

    const cancelRule = () => {
        if (isDefaultRule) {
            redirectToAssetsPage();
            return;
        }
        if (hasRuleChanged()) {
            setCancelFrictionVisible(true);
        } else {
            redirectToAssetsPage();
        }
    };
    
    const clearClauseBuilderValidation = () => {
        setIsValidated(false);
        setValidationErrorMsg(null);
        setShowValidationSuccessAlert(false);
    };

    const addClause = () => {
        const updateRule = deepCopy(ruleToEdit);
        updateRule.schemaClause.push({});
        setRuleToEdit(updateRule);
        clearClauseBuilderValidation();
    };

    const header = (
        <Header
            variant='h1'
            description={isDefaultRule ? constants.DEFAULT_RULE_NOTE : null}
        >
            {ruleId ? constants.EDIT_BUTTON_TEXT : constants.CREATE_BUTTON_TEXT} {constants.RULE_CAPITALIZED}
        </Header>
    );

    const formActions = isDefaultRule ? (
        <Button onClick={cancelRule}>{constants.CLOSE_BUTTON_TEXT}</Button>
    ) : (
        <SpaceBetween size='s' direction='horizontal'> 
            <Button variant='link' onClick={cancelRule}>{constants.CANCEL_BUTTON_TEXT}</Button>
            { isValidated ? ( 
                <Button
                    onClick={saveRule}
                    variant='primary'
                    loading={validateOrSaveLoading}
                >{`${ruleId ? constants.SAVE_BUTTON_TEXT : constants.CREATE_BUTTON_TEXT} ${constants.RULE}`}
                </Button>
            ) : (
                <Button
                    data-testid='validateRuleBtn'
                    onClick={validateRule}
                    loading={validateOrSaveLoading}
                >
                    {constants.VALIDATE} {constants.RULE}
                </Button>
            )
            }
        </SpaceBetween>
    );

    const impactSeverityChanged = (impactArea: ImpactArea) => ({ detail }: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
        const updatedRule = deepCopy(ruleToEdit);
        const updatedImpact = updatedRule.impacts.find((impact: Impact) => impact.area === impactArea);
        if (updatedImpact) {
            updatedImpact.severity = detail.selectedOption.value;
        }
        setRuleToEdit(updatedRule);
    };

    const getImpactSeverity = (): JSX.Element | null => {
        if (selectedImpacts.length === 0 || !ruleToEdit?.impacts) {
            return null;
        }
        const impactSeverities = ruleToEdit?.impacts.map(impact => {
            const matchingImpactOption = selectedImpacts.find(impactOption => impactOption.value === impact.area);
            if (!matchingImpactOption) {
                return null;
            }
            let selectedSeverity = severityOptions.find(severityOption => impact.severity === severityOption.value);
            if (!selectedSeverity) {
                selectedSeverity = defaultSeverityOption;
            }
            return (
                <Grid gridDefinition={[{colspan: 2}, {colspan: 2}]} key={matchingImpactOption.label}>
                    <Box>{matchingImpactOption.label}</Box>
                    <Select
                        options={severityOptions}
                        selectedOption={selectedSeverity}
                        onChange={impactSeverityChanged(impact.area)}
                        disabled={isDefaultRule}
                    />
                </Grid>
            );
        });
        return (
            <Container>
                <SpaceBetween size='s'>
                    {impactSeverities}
                </SpaceBetween>
            </Container>
        );
    };

    const selectedImpactsChanged = ({ detail }: NonCancelableCustomEvent<MultiselectProps.MultiselectChangeDetail>) => {
        setSelectedImpacts(detail.selectedOptions as OptionDefinition[]);
        if (!ruleToEdit) {
            return;
        }
        setConfigErrorText(null);
        const updatedRule = deepCopy(ruleToEdit);

        const ruleImpactLength = updatedRule?.impacts?.length ?? 0;
        if (detail.selectedOptions.length > ruleImpactLength) { // impact area was added
            const addedImpactOption = detail.selectedOptions.find(selectedImpact => 
                !updatedRule.impacts.find((impact: Impact) => impact.area === selectedImpact.value));
            if (addedImpactOption) {
                const impact: Impact = {
                    area: addedImpactOption.value as ImpactArea,
                    severity: ImpactSeverity.HIGH
                };
                const index = impactOptions.findIndex(option => option.value === addedImpactOption.value);
                updatedRule.impacts.splice(index, 0, impact);
            }
        } else { // impact area was removed
            const removedImpactOption = updatedRule.impacts.find((impact: Impact) => 
                !detail.selectedOptions.find(impactOption => impactOption.value === impact.area));
            if (removedImpactOption) {
                updatedRule.impacts.splice(updatedRule.impacts.indexOf(removedImpactOption), 1);
            }
        }
        setRuleToEdit(updatedRule);
    };

    const ruleConfigContainer = (
        <Container 
            header={
                <Header variant='h2'>
                    {constants.RULE_CONFIGURATION}
                </Header>
            }
        >
            <SpaceBetween size='m'>
                <FormField
                    label={constants.RULE_NAME}
                    description={constants.RULE_NAME_DESCRIPTION}
                >
                    <Input
                        value={ruleToEdit?.name ?? ''}
                        disabled={isDefaultRule}
                        onChange={({ detail }) => {
                            const rule: DataQualityGetRule = deepCopy(ruleToEdit);
                            rule.name = detail.value;
                            setRuleToEdit(rule);
                            setConfigErrorText(null);
                        }}
                    />
                </FormField>
                <FormField
                    label={constants.DESCRIPTION}
                    description={constants.DESCRIPTION_NOTE}
                >
                    <Textarea
                        value={ruleToEdit?.description ?? ''}
                        disabled={isDefaultRule}
                        onChange={({ detail }) => {
                            const rule: DataQualityGetRule = deepCopy(ruleToEdit);
                            rule.description = detail.value;
                            setRuleToEdit(rule);
                            setConfigErrorText(null);
                        }}
                    />
                </FormField>
                <FormField
                    label={constants.IMPACT}
                    description={constants.IMPACT_DESCRIPTION}
                >
                    <SpaceBetween size='s'>
                        <Multiselect
                            options={impactOptions}
                            selectedOptions={selectedImpacts}
                            onChange={selectedImpactsChanged}
                            keepOpen={false}
                            disabled={isDefaultRule}
                        />
                        {getImpactSeverity()}
                    </SpaceBetween>
                </FormField>
            </SpaceBetween>
        </Container>
    );

    const areSchemaEqual = (schemaA: SchemaClause, schemaB: SchemaClause): boolean => {
        return schemaA.attribute == schemaB.attribute && schemaA.attributeType == schemaB.attributeType
            && schemaA.firstGroup == schemaB.firstGroup && schemaA.operator == schemaB.operator
            && schemaA.secondGroup == schemaB.secondGroup && schemaA.value == schemaB.value;
    };

    const onClauseBuilderItemChanged = (currentItem: SchemaClause, updatedItem?: SchemaClause, isCopy = false) => {
        clearClauseBuilderValidation();
        const updatedRule: DataQualityGetRule = deepCopy(ruleToEdit);
        const index = updatedRule.schemaClause?.findIndex(item => areSchemaEqual(item, currentItem));

        if (index != undefined && index > -1) {
            if (updatedItem) { //update row
                updatedRule.schemaClause?.splice(index, 1, updatedItem);
            } else if (isCopy) { //copy row
                updatedRule.schemaClause?.splice(index, 0, deepCopy(currentItem));
            } else { // delete row
                updatedRule.schemaClause?.splice(index, 1);
            }
        }
        // check if schemaClause is empty and add empty row
        if (updatedRule.schemaClause?.length === 0) {
            updatedRule.schemaClause.push(emptySchemaClause);
        }

        setRuleToEdit(updatedRule);
    };

    const clauseBuilderActions = (
        <SpaceBetween size='s' direction='horizontal'>
            <Button onClick={toggleShowRawSchema}>{constants.SHOW_RAW_SCHEMA}</Button>
            <Button onClick={addClause}><Icon name='add-plus'/> {constants.ADD_CLAUSE}</Button>
        </SpaceBetween>
    );

    const clauseBuilderContainer = (
        <Container
            header={
                <Header 
                    variant='h2'
                    actions={clauseBuilderActions}
                >
                    {constants.CLAUSE_BUILDER}
                </Header>
            }
        >
            <Table
                items={ruleToEdit?.schemaClause ?? []}
                columnDefinitions={getClauseBuilderColumns(isDefaultRule ?? true, attributes, onClauseBuilderItemChanged)}
            />
        </Container>
    );

    const getRawSchemaAsString = (): string => {
        return JSON.stringify(ruleToEdit?.ajvSchema, null, 4) ?? '';
    };

    const getRawSchemaAsStringNumRows = (): number => {
        return getRawSchemaAsString().split('\n').length;
    };

    const rawSchemaActions = isDefaultRule ? null : (
        <Button 
            onClick={toggleShowRawSchema}
        >
            {constants.HIDE}
        </Button>
    );

    const rawSchemaContainer = (
        <Container
            header={
                <Header 
                    variant='h2'
                    actions={rawSchemaActions}
                    description={constants.RAW_SCHEMA_EDIT_NOT_SUPPORTED}
                >
                    {constants.RAW_SCHEMA}
                </Header>
            }
        >
            <Textarea 
                value={getRawSchemaAsString()}
                rows={getRawSchemaAsStringNumRows()}
                disabled
            />
        </Container>
    );

    return (loadingRuleDetails || loadingAttributes) ? <Spinner size='big'/> : (
        <Form
            header={header}
            actions={formActions}
        >
            <SpaceBetween size='m'>
                {ruleConfigContainer}
                {isClauseBuilderShown ? clauseBuilderContainer : rawSchemaContainer}
                {validationErrorMsg ? <Alert type='error' header={validationErrorMsg}/> : null}
                {showValidationSuccessAlert ? (
                    <Alert
                        data-testid='validationSuccessAlert'
                        type='success' 
                        header={constants.VALIDATION_SUCCESS} 
                        dismissible
                        onDismiss={() => setShowValidationSuccessAlert(false)}
                    />
                ) : null}
                {configErrorText ? <Alert type='error' header={configErrorText}/> : null}
            </SpaceBetween>
            { cancelFrictionVisible ? (
                <ConfirmationModal
                    confirmButton={constants.CONTINUE_BUTTON_TEXT}
                    onConfirm={redirectToAssetsPage}
                    onDismiss={() => { setCancelFrictionVisible(false); }}
                    header={constants.UNSAVED_CHANGES_WILL_BE_LOST}
                    visible
                >
                    {constants.UNSAVED_CHANGES_WILL_BE_LOST_CONTINUE}
                </ConfirmationModal>
            ) : null }
        </Form>
    );
}

export default withFlashContext(DataQualityCreateOrEditRule);
